import React, { useState } from 'react';
import { useQuery, useMutation, useSubscription } from '@apollo/client';
import { ANNOUNCEMENTS, POST_ANNOUNCEMENT, NEW_ANNOUNCEMENT_SUB } from '../graphql';
import { Megaphone, Pin, Plus, AlertTriangle, Calendar, BookOpen, Sun, Info } from 'lucide-react';

const TYPE_CONFIG = {
  GENERAL:      { label: 'General',         color: 'bg-blue-100 text-blue-700',    icon: Info },
  CANCELLATION: { label: 'Cancellation',    color: 'bg-red-100 text-red-700',      icon: AlertTriangle },
  EXAM_CHANGE:  { label: 'Exam Change',     color: 'bg-orange-100 text-orange-700',icon: Calendar },
  HOLIDAY:      { label: 'Holiday',         color: 'bg-green-100 text-green-700',  icon: Sun },
  URGENT:       { label: 'Urgent',          color: 'bg-red-200 text-red-800',      icon: AlertTriangle },
};

export default function AnnouncementsPage() {
  const [filter, setFilter] = useState('');
  const { data, loading, refetch } = useQuery(ANNOUNCEMENTS, { variables: { type: filter || null } });
  const [postAnnouncement] = useMutation(POST_ANNOUNCEMENT, { refetchQueries: [ANNOUNCEMENTS] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', type: 'GENERAL', subject: '', pinned: false });

  useSubscription(NEW_ANNOUNCEMENT_SUB, { onData: () => refetch() });

  const handlePost = async (e) => {
    e.preventDefault();
    await postAnnouncement({ variables: { ...form, pinned: form.pinned } });
    setForm({ title: '', body: '', type: 'GENERAL', subject: '', pinned: false });
    setShowForm(false);
  };

  const announcements = data?.announcements || [];
  const pinned = announcements.filter(a => a.pinned);
  const rest   = announcements.filter(a => !a.pinned);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="text-gray-500 text-sm mt-1">Real-time updates — class cancellations, exam changes, holidays</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
          <Plus size={18} /> Post Update
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['', ...Object.keys(TYPE_CONFIG)].map(t => (
          <button key={t} onClick={() => setFilter(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === t ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {t ? TYPE_CONFIG[t].label : 'All'}
          </button>
        ))}
      </div>

      {showForm && (
        <form onSubmit={handlePost} className="card mb-6 space-y-4">
          <h3 className="font-semibold">Post Announcement</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input className="input" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Physics class cancelled today" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select className="input" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                {Object.entries(TYPE_CONFIG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject (optional)</label>
              <input className="input" value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} placeholder="Data Structures" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
              <textarea className="input !h-24 resize-none" required value={form.body} onChange={e=>setForm({...form,body:e.target.value})} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.pinned} onChange={e=>setForm({...form,pinned:e.target.checked})} className="rounded" />
              <Pin size={14} /> Pin to top
            </label>
            <button className="btn-primary" type="submit">Post</button>
            <button className="btn-secondary" type="button" onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Pinned */}
      {pinned.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            <Pin size={12} /> Pinned
          </div>
          <div className="space-y-3">
            {pinned.map(a => <AnnouncementCard key={a.id} a={a} />)}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="space-y-3">
          {rest.map(a => <AnnouncementCard key={a.id} a={a} />)}
          {announcements.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Megaphone size={48} className="mx-auto mb-3 opacity-30" />
              <p>No announcements yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnnouncementCard({ a }) {
  const cfg = TYPE_CONFIG[a.type] || TYPE_CONFIG.GENERAL;
  const Icon = cfg.icon;
  return (
    <div className={`card border-l-4 ${a.type === 'URGENT' || a.type === 'CANCELLATION' ? 'border-red-400' : a.type === 'HOLIDAY' ? 'border-green-400' : a.type === 'EXAM_CHANGE' ? 'border-orange-400' : 'border-brand-400'}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`badge ${cfg.color} flex items-center gap-1`}>
            <Icon size={11} /> {cfg.label}
          </span>
          {a.subject && <span className="badge bg-gray-100 text-gray-500">{a.subject}</span>}
          {a.pinned && <Pin size={12} className="text-gray-400" />}
        </div>
        <span className="text-xs text-gray-400">{new Date(a.createdAt).toLocaleDateString()}</span>
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{a.title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{a.body}</p>
      <p className="text-xs text-gray-400 mt-2">— {a.author.name} {a.author.college ? `· ${a.author.college}` : ''}</p>
    </div>
  );
}
